// src/app/layout/sidebar/sidebar.component.ts
import { Component, inject, signal, computed, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Select } from 'primeng/select';
import { APP_ROUTES } from '../../core/models/constants';
import { AuthService } from '../../core/services/api/auth.service';
import { LayoutService } from '../layout.service';
import { DivisionService } from '../../core/services/api/entity.service';
import { Division } from '../../core/models/response';

interface NavItem {
    label: string;
    icon: string;
    route: string;
    roles?: string[];
    badge?: number;
    exact?: boolean;
}

interface NavSection {
    label: string;
    roles?: string[]; // si se define, la sección solo aparece para esos roles
    items: NavItem[];
}

@Component({
    selector: 'app-sidebar',
    standalone: true,
    imports: [CommonModule, RouterModule, RouterLinkActive, Select],
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
    auth = inject(AuthService);
    private router = inject(Router);
    layout = inject(LayoutService);
    private divisionService = inject(DivisionService);

    collapsed = computed(() => this.layout.sidebarCollapsed());
    mobileOpen = signal(false);
    divisions = signal<Division[]>([]);

    divisionOptions = computed(() => [
        { label: 'Todas', value: null },
        ...this.divisions().map(d => ({ label: d.name, value: d.idDivision }))
    ]);

    selectedDivision = computed(() => this.layout.selectedDivisionId());

    up = RouterLinkActive;

    constructor() {
        this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
            this.mobileOpen.set(false);
        });
    }

    ngOnInit() {
        if (this.auth.isSupervisor()) {
            this.divisionService.getAll().subscribe({
                next: (divs) => this.divisions.set(divs),
                error: () => {}
            });
        }
        // Restaurar división guardada, o null si no hay
        const saved = localStorage.getItem('selectedDivisionId');
        const savedName = localStorage.getItem('selectedDivisionName');
        if (saved) {
            this.layout.selectedDivisionId.set(Number(saved));
            this.layout.selectedDivisionName.set(savedName);
        } else {
            this.layout.selectedDivisionId.set(null);
            this.layout.selectedDivisionName.set(null);
        }
    }

    onDivisionChange(value: number | null) {
        if (value) {
            const div = this.divisions().find(d => d.idDivision === value);
            this.layout.selectedDivisionId.set(value);
            this.layout.selectedDivisionName.set(div?.name ?? null);
            localStorage.setItem('selectedDivisionId', String(value));
            localStorage.setItem('selectedDivisionName', div?.name ?? '');
        } else {
            this.layout.selectedDivisionId.set(null);
            this.layout.selectedDivisionName.set(null);
            localStorage.removeItem('selectedDivisionId');
            localStorage.removeItem('selectedDivisionName');
        }
    }

    // ── Estructura de navegación con secciones ─────────────────
    readonly navSections: NavSection[] = [
        {
            label: 'Principal',
            roles: ['ADMIN', 'SUPERVISOR', 'TECNICO', 'SOLICITANTE', 'CONTABILIDAD', 'RRHH'],
            items: [
                { label: 'Dashboard', icon: 'pi pi-home', route: APP_ROUTES.DASHBOARD, exact: true, roles: ['ADMIN', 'SUPERVISOR', 'TECNICO'] },
                { label: 'Tickets', icon: 'pi pi-ticket', route: APP_ROUTES.TICKETS },
                { label: 'Presupuestos', icon: 'pi pi-file-edit', route: APP_ROUTES.PRESUPUESTOS, roles: ['ADMIN', 'SUPERVISOR', 'TECNICO', 'CONTABILIDAD'] },
                { label: 'Mantenimientos', icon: 'pi pi-wrench', route: '/maintenance', roles: ['ADMIN', 'SUPERVISOR', 'TECNICO', 'CONTABILIDAD', 'RRHH'] },
                { label: 'Mis Actividades', icon: 'pi pi-clock', route: '/activities', roles: ['ADMIN', 'SUPERVISOR', 'TECNICO'] }
            ]
        },
        {
            label: 'Equipamiento',
            roles: ['ADMIN', 'SUPERVISOR', 'TECNICO'],
            items: [
                { label: 'Equipos', icon: 'pi pi-desktop', route: APP_ROUTES.EQUIPMENT },
                { label: 'Procesos', icon: 'pi pi-list-check', route: '/maintenance-processes', exact: true },
                { label: 'Herramientas', icon: 'pi pi-wrench', route: '/tools', roles: ['ADMIN', 'SUPERVISOR'] },
                { label: 'Jornadas', icon: 'pi pi-clock', route: '/work-sessions', roles: ['ADMIN', 'SUPERVISOR'] }
            ]
        },
        {
            label: 'Caja Chica',
            roles: ['ADMIN', 'SUPERVISOR', 'TECNICO', 'RRHH', 'CONTABILIDAD'],
            items: [
                { label: 'Caja Chica', icon: 'pi pi-wallet', route: '/cash-registers' }
            ]
        },
        {
                label: 'Configuración',
            roles: ['ADMIN', 'SUPERVISOR', 'RRHH', 'CONTABILIDAD'],
            items: [
                { label: 'Entidades', icon: 'pi pi-building', route: APP_ROUTES.ENTITIES, roles: ['ADMIN', 'SUPERVISOR', 'RRHH'] },
                { label: 'Sucursales', icon: 'pi pi-map-marker', route: APP_ROUTES.BRANCHES, roles: ['ADMIN', 'SUPERVISOR', 'RRHH'] },
                { label: 'Divisiones', icon: 'pi pi-sitemap', route: APP_ROUTES.DIVISIONS, roles: ['ADMIN', 'SUPERVISOR', 'RRHH'] },
                { label: 'Categorías Facturación', icon: 'pi pi-tags', route: APP_ROUTES.BILLING_CATEGORIES, roles: ['ADMIN', 'CONTABILIDAD', 'RRHH'] }
            ]
        },
        {
            label: 'Administración',
            items: [
                { label: 'Usuarios', icon: 'pi pi-users', route: APP_ROUTES.USERS, roles: ['ADMIN', 'RRHH'] },
                { label: 'Reportes', icon: 'pi pi-chart-bar', route: APP_ROUTES.REPORTS, roles: ['ADMIN', 'RRHH', 'CONTABILIDAD'] }
            ]
        }
    ];

    // Filtra secciones y sus items según el rol actual
    visibleSections = computed(() => {
        const role = this.auth.role();
        return this.navSections
            .filter((section) => !section.roles || section.roles.includes(role))
            .map((section) => ({
                ...section,
                items: section.items.filter((item) => !item.roles || item.roles.includes(role))
            }))
            .filter((section) => section.items.length > 0);
    });

    @HostListener('window:resize')
    onResize() {
        if (window.innerWidth >= 1024) this.mobileOpen.set(false);
    }

    toggleCollapse() {
        this.layout.sidebarCollapsed.update((v) => !v);
    }
    toggleMobile() {
        this.mobileOpen.update((v) => !v);
    }
    closeMobile() {
        this.mobileOpen.set(false);
    }
    logout() {
        this.auth.logout();
    }
}
